import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PersonalTask } from '../../api/personal-tasks';

interface PersonalTasksState {
  loading: boolean;
  error: string | null;
  data: PersonalTask[];
  current: PersonalTask | null;
  deleting: boolean;
  updating: boolean;
  currentError: string | null;
  creating: boolean;
  createError: string | null;
}

const INITIAL_STATE: PersonalTasksState = {
  loading: false,
  error: null,
  data: [],
  current: null,
  deleting: false,
  updating: false,
  currentError: null,
  creating: false,
  createError: null,
};

const updateCase = (
  state: PersonalTasksState,
  { payload }: PayloadAction<PersonalTask>,
): PersonalTasksState => ({
  ...state,
  current: payload,
  updating: true,
});

const requestComplete = (state: PersonalTasksState): PersonalTasksState => ({
  ...state,
  updating: false,
  current: null,
});

export interface UpdatePayload {
  id: number;
  data: Partial<PersonalTask>;
}

export const personalTasksSlice = createSlice({
  initialState: INITIAL_STATE,
  name: 'personalTasks',

  reducers: (create) => ({
    fetchStart: create.reducer((state: PersonalTasksState) => ({
      ...state,
      loading: true,
    })),

    fetchSuccess: create.reducer(
      (state, { payload }: PayloadAction<PersonalTask[]>) => ({
        ...state,
        data: payload,
        loading: false,
      }),
    ),

    createStart: create.reducer((state: PersonalTasksState) => ({
      ...state,
      creating: true,
    })),

    create: create.reducer(
      (
        state: PersonalTasksState,
        { payload }: PayloadAction<PersonalTask>,
      ) => ({
        ...state,
        data: [payload, ...state.data],
      }),
    ),

    createSuccess: create.reducer((state: PersonalTasksState) => ({
      ...state,
      creating: false,
      createError: null,
    })),

    createFailure: create.reducer((state: PersonalTasksState) => ({
      ...state,
      creating: false,
      createError: 'error',
    })),

    deleteStart: create.reducer(
      (
        state: PersonalTasksState,
        { payload }: PayloadAction<PersonalTask>,
      ) => ({
        ...state,
        current: payload,
        deleting: true,
      }),
    ),

    deleteSuccess: create.reducer(
      (state, { payload }: PayloadAction<PersonalTask>) => ({
        ...state,
        data: [
          ...state.data.filter(
            (d) => d.personalTaskId !== payload.personalTaskId,
          ),
        ],
        current: null,
        deleting: false,
      }),
    ),

    deleteFailed: create.reducer((state: PersonalTasksState) => ({
      ...state,
      current: null,
      deleting: false,
    })),

    updateStart: create.reducer(updateCase),

    update: create.reducer(
      (
        state: PersonalTasksState,
        { payload }: PayloadAction<UpdatePayload>,
      ): PersonalTasksState => ({
        ...state,
        data: [...state.data].reduce((arr: PersonalTask[], next) => {
          let task = { ...next };

          if (task.personalTaskId === payload.id) {
            task = Object.assign(task, payload.data);
          }

          arr.push(task);

          return arr;
        }, []),
        updating: false,
        current: null,
      }),
    ),

    updateSuccess: create.reducer(requestComplete),
    updateFailed: create.reducer(requestComplete),
  }),
});
